import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, setApiTokenOverride } from "../lib/api";
import LeadPreventivo from "./LeadPreventivo";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Loader2, Briefcase, Send, Copy, Printer, CheckCircle2, Presentation, FileSignature } from "lucide-react";

function PublishBox({ result, busy, sending, canPublish, onPublish, onSend, demo, signed }) {
  return (
    <div className="rounded border border-border p-4 space-y-3" data-testid="rete-publish-box">
      <Button onClick={onPublish} disabled={busy || !canPublish} data-testid="rete-publish-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Presentation className="h-4 w-4 mr-1.5" />} {result ? "Aggiorna proposta cliente" : "Genera proposta cliente"}
      </Button>
      {result && (
        <div className="space-y-2" data-testid="rete-publish-result">
          <div className="flex items-center gap-2 text-primary text-sm font-semibold"><CheckCircle2 className="h-4 w-4" /> Proposta pronta · link senza scadenza</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={onSend} disabled={demo || sending || result.email_sent || !result.email_to} title={demo ? "Consulenza demo: invio al cliente disattivato" : undefined} data-testid="rete-send-email">
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}{result.email_sent ? "Email inviata" : "Invia email al cliente"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${result.url}`); toast.success("Link copiato"); }} data-testid="rete-copy-link"><Copy className="h-4 w-4 mr-1.5" /> Copia link</Button>
            <a href={result.url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" data-testid="rete-open-proposal">Apri proposta</Button></a>
            <a href={result.print_url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" data-testid="rete-print"><Printer className="h-4 w-4 mr-1.5" /> PDF proposta</Button></a>
          </div>
          {demo && <p className="text-[11px] text-amber-500" data-testid="rete-demo-send-note">Consulenza demo: l'invio al cliente è disattivato. Per un cliente reale crea la pratica con "Nuovo cliente".</p>}
          {signed && (
            <a href={`${result.url}/contratto`} target="_blank" rel="noopener noreferrer">
              <Button size="sm" data-testid="rete-signed-contract-pdf"><FileSignature className="h-4 w-4 mr-1.5" /> Contratto firmato (PDF)</Button>
            </a>
          )}
          {!result.email_to && <p className="text-[11px] text-muted-foreground">Il cliente non ha un indirizzo email: copia il link o scarica il PDF.</p>}
        </div>
      )}
    </div>
  );
}

// Scheda di costo Fier per l'installatore partner della rete, con prezzi riservati sugli articoli a listino.
export default function ReteCostEditor({ token, leadId, onChanged }) {
  const [lead, setLead] = useState(null);
  const prevUpd = useRef(null);
  useEffect(() => {
    const u = lead?.updated_at;
    if (prevUpd.current && u && u !== prevUpd.current) onChanged?.();
    prevUpd.current = u || prevUpd.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.updated_at]);
  const [listino, setListino] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [demo, setDemo] = useState(false);

  useEffect(() => {
    setApiTokenOverride(localStorage.getItem("rete_session"));
    (async () => {
      try {
        const { data: qc } = await api.get(`/rete/c/${token}/quote-config`);
        setDemo(!!qc.demo);
        if (qc.outlook_url) setResult({ url: qc.outlook_url, print_url: `${qc.outlook_url}/print`, email_to: qc.email_to, email_sent: qc.email_sent });
        const [{ data: l }, { data: li }] = await Promise.all([api.get(`/leads/${leadId}`), api.get(`/rete/c/${token}/listino`)]);
        setLead(l); setListino(li.items || []);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || "Accesso rete non valido");
      }
    })();
    return () => setApiTokenOverride(null);
  }, [leadId, token]);

  // Rientrando nella scheda (es. dopo aver modificato il layout) ricarica la pratica aggiornata.
  useEffect(() => {
    const onVis = async () => {
      if (document.visibilityState !== "visible") return;
      setApiTokenOverride(localStorage.getItem("rete_session"));
      try { const { data: l } = await api.get(`/leads/${leadId}`); setLead(l); } catch { /* resta il dato corrente */ }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const publish = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/rete/c/${token}/publish`); setResult(data); toast.success("Proposta generata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generazione non riuscita"); }
    finally { setBusy(false); }
  };
  const activeSent = !!((lead?.preventivi || []).find((p) => p.id === lead?.active_preventivo_id) || {}).locked;
  const sendEmail = async () => {
    setSending(true);
    try { const { data } = await api.post(`/rete/c/${token}/send-proposal-email`); const { data: l } = await api.get(`/leads/${leadId}`); setLead(l); toast.success(`Proposta inviata (${data.email_to})${data.cc?.length ? ` · copia a ${data.cc.join(", ")}` : ""}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio email non riuscito"); }
    finally { setSending(false); }
  };

  if (err) return <Card className="p-5 mt-4 text-sm text-muted-foreground" data-testid="rete-editor-error">{err}</Card>;
  if (!lead) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded border-primary/40 bg-card p-5 mt-6 space-y-5" data-testid="rete-cost-editor">
      <div className="flex items-center gap-2 text-primary font-semibold"><Briefcase className="h-4 w-4" /> Scheda di costo e proposta (installatore partner)</div>
      <LeadPreventivo lead={lead} onUpdate={setLead} canEdit reteEditor listino={listino} />
      <PublishBox result={result ? { ...result, email_sent: activeSent } : result} busy={busy} sending={sending} canPublish={!!lead.cost_estimate?.rete_partner && !activeSent} onPublish={publish} onSend={sendEmail} demo={demo} signed={!!lead.contract?.accettazione?.accettato} />
    </Card>
  );
}
